// TAX2-S2 (docs/build-briefs/TAX2-S2-sales-tax-worksheet.md; DEC-303, DEC-304; design/mockups/
// tax2-1-september.html, tax2-2-rate-change.html): the body of the Sales tax worksheet, drawn inside
// the Reports group's own frame (ui/src/reports.ts owns the title, the Download CSV button and the
// loading and error states). This module only draws what the server sent, through the rules in
// salesTaxWorksheetModel.ts, and reports every tap back through `handlers`.
//
// An internal worksheet that never files: there is no Filed button, state or wording anywhere here.
//
// Admin surface, touch-sized: every control is a 44px button, nothing depends on hover, nothing is in
// a tooltip, and there is no keyboard listener. Text is set with textContent only. The only part
// that scrolls sideways is the Day by day table inside its own box; the page never does.

import { zoneFriendlyName } from "./discountSpecialEditor.ts";
import {
  HOW_COUNTED,
  PERIOD_KINDS,
  basisLabel,
  basisLowerLabel,
  businessDayText,
  buildChecks,
  buildDayRows,
  buildE500Rows,
  buildTotalRow,
  buildWaysToCount,
  canShowNextSelection,
  canShowPreviousSelection,
  checkSummary,
  customRangeAsk,
  filingFrequencyKind,
  passedChecksLabel,
  selectionFromReport,
  selectionLabel,
  shortDay,
  stripPills,
  type CheckRow,
  type PeriodKind,
  type SalesTaxReportDto,
  type WorksheetBasis,
} from "./salesTaxWorksheetModel.ts";
import { formatMoney } from "./reportFormat.ts";

export interface SalesTaxHandlers {
  onPrevious: () => void;
  onNext: () => void;
  onKind: (kind: PeriodKind) => void;
  /** Two whole dates, both real and in order (the screen never asks for anything else). */
  onCustom: (from: string, to: string) => void;
  onBasis: (basis: WorksheetBasis) => void;
  /** Saves the period kind or the date basis on screen as the location's default. */
  onSaveDefault: (which: "period" | "basis") => void;
}

export interface SalesTaxView {
  /** When the figures were loaded, for the "Updated" line. */
  loadedAt: number;
  /** The session holds admin.tax_rate.manage: only then is "Make default" in the page at all. */
  canSaveDefaults: boolean;
  saving: boolean;
  saveFailed: boolean;
}

interface Opts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function h(tag: string, opts: Opts = {}, children: Array<Node | string> = []): HTMLElement {
  const element = document.createElement(tag);
  if (opts.cls !== undefined) element.className = opts.cls;
  if (opts.text !== undefined) element.textContent = opts.text;
  if (opts.testid !== undefined) element.dataset["testid"] = opts.testid;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) {
    element.setAttribute(key, value);
  }
  for (const child of children) {
    element.append(child);
  }
  return element;
}

function clear(element: HTMLElement): void {
  while (element.firstChild !== null) {
    element.removeChild(element.firstChild);
  }
}

/** "today 8:02 AM" or "Oct 6 8:02 AM", in the browser's own clock (a freshness hint, not a tax date). */
function updatedText(loadedAt: number): string {
  const when = new Date(loadedAt);
  const time = when.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const sameDay = when.toDateString() === new Date().toDateString();
  return sameDay
    ? `today ${time}`
    : `${when.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${time}`;
}

export function renderSalesTaxWorksheet(
  root: HTMLElement,
  report: SalesTaxReportDto,
  view: SalesTaxView,
  handlers: SalesTaxHandlers,
): void {
  clear(root);
  const { worksheet, context } = report;
  // The default zone reads "GMT+00:00" from the browser, which means nothing to a manager: say UTC.
  const friendlyZone = worksheet.checks.timeZone.isUtc
    ? "UTC"
    : zoneFriendlyName(worksheet.timeZone);
  const checks = buildChecks(worksheet, context, friendlyZone);
  const summary = checkSummary(checks);
  const selection = selectionFromReport(report);
  const periodText = selectionLabel(selection);
  const container = h("div", { cls: "stw", testid: "stw-root" });

  // ---- Controls: location, period, count a sale by ----
  const kindButtons = PERIOD_KINDS.map(({ kind, label }) => {
    const button = h("button", {
      cls: selection.kind === kind ? "on" : "",
      text: label,
      testid: `stw-kind-${kind}`,
      attrs: { type: "button", "aria-pressed": String(selection.kind === kind) },
    });
    button.addEventListener("click", () => {
      if (selection.kind !== kind) handlers.onKind(kind);
    });
    return button;
  });
  const periodBody: HTMLElement[] = [];
  if (selection.kind === "custom") {
    const fromInput = h("input", {
      cls: "field-input stw-date",
      testid: "stw-custom-from",
      attrs: {
        type: "date",
        value: selection.from,
        max: selection.to,
        "aria-label": "From",
        "data-setting-exempt": "report date filter",
      },
    }) as HTMLInputElement;
    const toInput = h("input", {
      cls: "field-input stw-date",
      testid: "stw-custom-to",
      attrs: {
        type: "date",
        value: selection.to,
        min: selection.from,
        "aria-label": "To",
        "data-setting-exempt": "report date filter",
      },
    }) as HTMLInputElement;
    const ask = (): void => {
      const valid = customRangeAsk(fromInput.value, toInput.value);
      fromInput.toggleAttribute("aria-invalid", !valid);
      toInput.toggleAttribute("aria-invalid", !valid);
      if (valid && (fromInput.value !== selection.from || toInput.value !== selection.to)) {
        handlers.onCustom(fromInput.value, toInput.value);
      }
    };
    fromInput.addEventListener("change", ask);
    toInput.addEventListener("change", ask);
    periodBody.push(
      h("div", { cls: "stw-dates" }, [
        h("label", { cls: "stw-datelab" }, [h("span", { text: "From" }), fromInput]),
        h("label", { cls: "stw-datelab" }, [h("span", { text: "To" }), toInput]),
      ]),
    );
  } else {
    const prev = h("button", {
      cls: "stw-arrow",
      text: "‹",
      testid: "stw-prev-month",
      attrs: { type: "button", "aria-label": "Previous period" },
    });
    if (!canShowPreviousSelection(selection)) prev.setAttribute("disabled", "");
    prev.addEventListener("click", handlers.onPrevious);
    const next = h("button", {
      cls: "stw-arrow",
      text: "›",
      testid: "stw-next-month",
      attrs: { type: "button", "aria-label": "Next period" },
    });
    if (!canShowNextSelection(selection, context.today)) {
      next.setAttribute("disabled", "");
    }
    next.addEventListener("click", handlers.onNext);
    periodBody.push(
      h("div", { cls: "stw-period" }, [
        prev,
        h("span", { cls: "stw-plabel", text: periodText, testid: "stw-period-label" }),
        next,
      ]),
    );
  }
  const basisButtons = (["business_date", "calendar_date"] as const).map((basis) => {
    const button = h("button", {
      cls: worksheet.basis === basis ? "on" : "",
      text: basisLabel(basis),
      testid: `stw-basis-${basis}`,
      attrs: { type: "button", "aria-pressed": String(worksheet.basis === basis) },
    });
    button.addEventListener("click", () => {
      if (worksheet.basis !== basis) handlers.onBasis(basis);
    });
    return button;
  });
  // The saved default: a "Default" tag where the screen matches it, else (owner function only) one "Make default" button.
  const defaultMark = (
    which: "period" | "basis",
    matches: boolean,
    offerable: boolean,
  ): HTMLElement[] => {
    if (!offerable) return [];
    if (matches) {
      return [h("span", { cls: "tag ok", text: "Default", testid: `stw-${which}-default` })];
    }
    if (!view.canSaveDefaults) return [];
    const button = h("button", {
      cls: "btn-quiet stw-makedefault",
      text: "Make default",
      testid: `stw-save-${which}`,
      attrs: { type: "button" },
    });
    if (view.saving) button.setAttribute("disabled", "");
    button.addEventListener("click", () => handlers.onSaveDefault(which));
    return [button];
  };
  const savedKind = filingFrequencyKind(context.filing.frequency);
  const periodMarks = defaultMark("period", selection.kind === savedKind, selection.kind !== "custom");
  const basisMarks = defaultMark("basis", worksheet.basis === context.filing.dateBasis, true);
  container.append(
    h("section", { cls: "stw-controls", attrs: { "aria-label": "Choose what to show" } }, [
      h("div", { cls: "stw-ctl" }, [
        h("span", { cls: "stw-clabel", text: "Location" }),
        h("span", { cls: "stw-loc", text: context.location.name, testid: "stw-location" }),
      ]),
      h("div", { cls: "stw-ctl" }, [
        h("span", { cls: "stw-clabel", text: "Period" }),
        h("div", { cls: "stw-ctlrow" }, [
          h("div", { cls: "stw-seg", attrs: { role: "group", "aria-label": "Period" } }, kindButtons),
          ...periodMarks,
        ]),
        ...periodBody,
      ]),
      h("div", { cls: "stw-ctl" }, [
        h("span", { cls: "stw-clabel", text: "Count a sale by" }),
        h("div", { cls: "stw-ctlrow" }, [
          h(
            "div",
            { cls: "stw-seg", attrs: { role: "group", "aria-label": "Date basis" } },
            basisButtons,
          ),
          ...basisMarks,
        ]),
      ]),
      ...(view.saveFailed
        ? [h("p", { cls: "stw-savefail", text: "Could not save. Try again.", testid: "stw-save-failed", attrs: { role: "alert" } })]
        : []),
    ]),
  );

  // ---- The strip ----
  const strip = h("div", { cls: "fresh", testid: "stw-strip" }, [
    h("span", {}, [h("b", { text: `${shortDay(worksheet.from)} to ${shortDay(worksheet.to)}` })]),
    h("span", { testid: "stw-business-day" }, [
      h("b", { text: "Business day" }),
      ` ${businessDayText(context.cutoffLocal, friendlyZone).replace(/^Business day /, "")}`,
    ]),
    h("span", {}, [h("b", { text: "Updated" }), ` ${updatedText(view.loadedAt)}`]),
  ]);
  for (const pill of stripPills(worksheet, context)) {
    strip.append(
      h("span", {
        cls: `tag ${pill.tone === "final" ? "ok" : pill.tone}`,
        text: pill.text,
        testid: "stw-pill",
      }),
    );
  }
  if (summary.problems > 0) {
    strip.append(h("span", { cls: "tag warn", text: summary.text, testid: "stw-strip-problems" }));
  }
  container.append(strip);

  // ---- The sales tax numbers card ----
  const sum = h("div", { cls: "stw-sum" });
  for (const row of buildE500Rows(worksheet, context)) {
    if (row.kind === "group") {
      sum.append(h("div", { cls: "stw-sgroup", text: row.title }));
      continue;
    }
    sum.append(
      h(
        "div",
        {
          cls: `stw-srow ${row.tone}${row.negative === true ? " negative" : ""}${row.line === "" ? " nobadge" : ""}`,
          testid: `stw-row-${row.key}`,
        },
        [
          ...(row.line === "" ? [] : [h("span", { cls: "stw-ln", text: row.line })]),
          h("span", { cls: "stw-sl" }, [
            h("b", { text: row.title }),
            h("small", { text: row.caption }),
          ]),
          h("span", { cls: "stw-sa", text: row.amount, testid: `stw-amount-${row.key}` }),
        ],
      ),
    );
  }
  const how = h("details", { cls: "stw-how", testid: "stw-how" }, [
    h("summary", { text: "How this is counted" }),
    h(
      "ul",
      {},
      HOW_COUNTED.map((line) => h("li", { text: line })),
    ),
  ]);
  const e500 = h(
    "section",
    {
      cls: "card stw-sumcard",
      testid: "stw-e500",
      attrs: { "aria-label": "Sales tax worksheet" },
    },
    [
      h("h2", {}, [
        "Your sales tax numbers ",
        h("small", { text: `${periodText}, by ${basisLowerLabel(worksheet.basis)}` }),
      ]),
      sum,
      how,
      h("p", {
        cls: "stw-adv",
        text: "Your advisor decides which basis to use. Ask before your first filing.",
      }),
    ],
  );

  // ---- The two ways to count ----
  const side = h("aside", { cls: "stw-side" });
  const ways = buildWaysToCount(worksheet, context);
  if (ways !== null) {
    const cell = (cents: number, selected: boolean) =>
      h("td", { cls: selected ? "selc" : "", text: formatMoney(cents) });
    side.append(
      h(
        "section",
        {
          cls: "card stw-cmp",
          testid: "stw-ways",
          attrs: { "aria-label": "Business date compared with calendar date" },
        },
        [
          h("h2", {}, [
            "The two ways to count ",
            h("small", {}, [h("span", { cls: "tag warn", text: "Totals differ" })]),
          ]),
          h("table", { cls: "stw-cmpt" }, [
            h("thead", {}, [
              h("tr", {}, [
                h("th", { cls: "l" }),
                h("th", { text: "Business date" }),
                h("th", { text: "Calendar date" }),
              ]),
            ]),
            h("tbody", {}, [
              h("tr", {}, [
                h("td", { cls: "l", text: "Taxable receipts" }),
                cell(ways.business.taxableCents, ways.selected === "business_date"),
                cell(ways.calendar.taxableCents, ways.selected === "calendar_date"),
              ]),
              h("tr", {}, [
                h("td", { cls: "l", text: "Total tax due" }),
                cell(ways.business.taxDueCents, ways.selected === "business_date"),
                cell(ways.calendar.taxDueCents, ways.selected === "calendar_date"),
              ]),
              h("tr", {}, [
                h("td", { cls: "l", text: "Difference" }),
                h("td", {
                  cls: "diff",
                  text: ways.differenceText,
                  attrs: { colspan: "2" },
                  testid: "stw-difference",
                }),
              ]),
            ]),
          ]),
          h("p", { cls: "stw-cnote", text: "Your accountant picks one. Use it every month." }),
        ],
      ),
    );
  }

  // ---- Checks ----
  const list = h("ul", { cls: "stw-chklist" });
  for (const check of checks.filter((row) => row.state !== "ok")) {
    list.append(renderCheck(check));
  }
  // Passing checks collapse into one tappable line; problems and neutral rows stay open.
  const passed = checks.filter((row) => row.state === "ok");
  const passedList = h("ul", {
    cls: "stw-chklist stw-passed-list",
    testid: "stw-passed-list",
    attrs: { hidden: "" },
  });
  for (const check of passed) {
    passedList.append(renderCheck(check));
  }
  const passedLabel = passedChecksLabel(passed.length);
  const passedButton = h("button", {
    cls: "stw-passed-toggle",
    text: `\u2713 ${passedLabel}`,
    testid: "stw-passed-toggle",
    attrs: { type: "button", "aria-expanded": "false" },
  });
  passedButton.addEventListener("click", () => {
    const open = passedList.hidden;
    passedList.hidden = !open;
    passedButton.setAttribute("aria-expanded", String(open));
    passedButton.textContent = open ? "Hide the passed checks" : `\u2713 ${passedLabel}`;
  });
  side.append(
    h(
      "section",
      { cls: "card stw-checks", testid: "stw-checks", attrs: { "aria-label": "Checks" } },
      [
        h("h2", {}, [
          "Checks ",
          h("small", {}, [
            h("span", {
              cls: `tag ${summary.tone === "ok" ? "ok" : summary.tone === "info" ? "info" : "warn"}`,
              text: summary.text,
              testid: "stw-checks-summary",
            }),
          ]),
        ]),
        list,
        ...(passed.length > 0 ? [passedButton, passedList] : []),
      ],
    ),
  );
  container.append(h("div", { cls: "stw-grid" }, [e500, side]));

  // ---- Day by day ----
  const total = buildTotalRow(worksheet, context);
  const headers = [
    `${basisLabel(worksheet.basis)}`,
    "Gross receipts",
    "Discounts and comps",
    "Refunds",
    "Taxable",
    "Tax collected",
    "Tax refunded",
    "Net tax",
    "Rate",
  ];
  const tbody = h("tbody");
  for (const row of buildDayRows(worksheet, context)) {
    const dateCell = h("td", { cls: "l stw-sticky" }, [
      h("div", { cls: "stw-dlab" }, [
        row.label,
        ...(row.note === "" ? [] : [h("small", { text: row.note })]),
      ]),
    ]);
    const money = (text: string, zero: boolean, cls = "") =>
      h("td", { cls: `${cls}${zero ? " zero" : ""}`.trim(), text });
    tbody.append(
      h("tr", { cls: row.highlighted ? "hl" : "", testid: `stw-day-${row.date}` }, [
        dateCell,
        money(row.cells.gross, false),
        money(row.cells.discounts, row.zero.discounts, "neg"),
        money(row.cells.refunds, row.zero.refunds, "neg"),
        money(row.cells.taxable, false, "strong"),
        money(row.cells.collected, false),
        money(row.cells.refunded, row.zero.refunded, "neg"),
        money(row.cells.net, false, "strong"),
        h("td", { text: row.cells.rate }),
      ]),
    );
  }
  tbody.append(
    h("tr", { cls: "total", testid: "stw-total-row" }, [
      h("td", { cls: "l stw-sticky", text: "Total" }),
      h("td", { text: total.gross }),
      h("td", { text: total.discounts }),
      h("td", { text: total.refunds }),
      h("td", { text: total.taxable }),
      h("td", { text: total.collected }),
      h("td", { text: total.refunded }),
      h("td", { text: total.net }),
      h("td", { text: total.rate }),
    ]),
  );
  container.append(
    h(
      "section",
      { cls: "card stw-daily", testid: "stw-daily", attrs: { "aria-label": "Day by day" } },
      [
        h("h2", {}, [
          "Day by day ",
          h("small", {
            text: `${basisLabel(worksheet.basis)}, ${worksheet.days.length} ${
              worksheet.days.length === 1 ? "day" : "days"
            }`,
          }),
        ]),
        h(
          "div",
          {
            cls: "stw-tblwrap hscroll",
            attrs: { tabindex: "0", role: "region", "aria-label": "Day by day table" },
          },
          [
            h("table", {}, [
              h("thead", {}, [
                h(
                  "tr",
                  {},
                  headers.map((text, index) =>
                    h("th", { cls: index === 0 ? "l stw-sticky" : "", text }),
                  ),
                ),
              ]),
              tbody,
            ]),
          ],
        ),
        h("p", {
          cls: "stw-foot",
          text: "Sales count on the day the card was charged. The rate is the rate in effect on the day of the sale or refund.",
        }),
      ],
    ),
  );
  root.append(container);
}

function renderCheck(check: CheckRow): HTMLElement {
  const mark = check.state === "ok" ? "✓" : check.state === "warn" ? "!" : "i";
  const text = h("div", { cls: "stw-ct" }, [
    h("b", { text: check.title, testid: `stw-check-title-${check.id}` }),
    h("span", { text: check.detail }),
  ]);
  const item = h(
    "li",
    {
      cls: `stw-chk ${check.state}`,
      testid: `stw-check-${check.id}`,
      attrs: { "data-state": check.state },
    },
    [
      h("span", { cls: `stw-ck ${check.state}`, text: mark, attrs: { "aria-hidden": "true" } }),
      text,
    ],
  );
  if (check.listButton !== undefined && check.listLines !== undefined) {
    const lines = check.listLines;
    const listEl = h(
      "ul",
      { cls: "stw-checklist-lines", testid: `stw-check-list-${check.id}`, attrs: { hidden: "" } },
      lines.map((line) => h("li", { text: line })),
    );
    const openLabel = check.listButton;
    const button = h("button", {
      cls: "btn-quiet stw-seebtn",
      text: openLabel,
      testid: `stw-check-button-${check.id}`,
      attrs: { type: "button", "aria-expanded": "false" },
    });
    button.addEventListener("click", () => {
      const open = listEl.hidden;
      listEl.hidden = !open;
      button.setAttribute("aria-expanded", String(open));
      button.textContent = open ? "Hide the list" : openLabel;
    });
    // One placement everywhere: the button sits below the text, then the list it opens.
    text.append(button, listEl);
  }
  return item;
}
